'use client';

import { PageContainerProvider } from '@magicschool/spellbook/components/PageContainer/PageContainerProvider';
import {
  PageContainerActionsSlot,
  PageContainerAlertSlot,
  PageContainerBackgroundSlot,
  PageContainerBottomSlot,
  PageContainerBreadcrumbSlot,
  PageContainerContentSlot,
  PageContainerDescriptionSlot,
  PageContainerPanelCloseSlot,
  PageContainerPanelSlot,
  PageContainerPanelToggleSlot,
  PageContainerTitleBadgeSlot,
  PageContainerTitleSlot,
  PageContainerTopActionsSlot,
  PageContainerTopNavSlot,
} from '@magicschool/spellbook/components/PageContainer/slots';
import type { BreadcrumbData } from '@magicschool/spellbook/components/PageHeader';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { SIDE_BY_SIDE_MIN_WIDTH } from '@magicschool/spellbook/hooks/usePanelResize';
import { useScroll } from '@magicschool/spellbook/hooks/useScroll';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactElement } from 'react';
import { Children, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type PageContainerPanelState, type PageContainerText } from './PageContainerProvider';

export type PageContainerLayout = 'standard' | 'fixed-height' | 'chat';

export type PageContainerProps = VariantProps<typeof pageContainerContentVariants> &
  // @todo change back to ComponentProps<'main'> once all routes supply id="main-content" via a surrounding layout
  ComponentProps<'div'> & {
    /** Breadcrumb trail entries. When provided, auto-renders the Breadcrumb slot. Last entry should be `type: 'page'`. */
    breadcrumbs?: BreadcrumbData[];
    /** Additional classes applied to the scrollable content area wrapper. */
    contentClassName?: string;
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: Partial<PageContainerText>;
    /**
     * Controls the Panel slot's open/closed state from the root. When provided, you do not need to pass
     * `isOpen` to `<PageContainer.Panel>` — the root prop drives it. Pair with `onPanelOpenChange`.
     */
    panelOpen?: boolean;
    /** Callback fired when the panel open state should change. Pair with `panelOpen`. */
    onPanelOpenChange?: (open: boolean) => void;
    /**
     * Controls the layout mode of the content area.
     * - `"standard"` (default): content area scrolls vertically. The `size` prop constrains the
     *   content column width and centers it with `mx-auto`.
     * - `"fixed-height"`: content area fills remaining flex space without scrolling. Use for chat or
     *   split-pane pages where an embedded component (e.g. `Chat`) owns its own internal scroll
     *   and height. The `size` prop still controls the chrome (header bar, heading, alert) max-width,
     *   but the content container (`<PageContainer.Content>`) is **always full-width** in this mode —
     *   equivalent to `size="xl"` on the content only. The child is responsible for its own centering.
     */
    layout?: PageContainerLayout;
    /**
     * Reserved for when `PageContainer` takes over the `<main>` role. Currently a no-op — `id` is
     * not applied to any element while the `@todo` migration is in progress. The surrounding layout
     * shell (`AppLayout`, `EmptyLayout`) is responsible for rendering `<main id="main-content">`.
     */
    mainId?: string;
    /**
     * When true, the page heading area sticks to the top of the viewport on narrow screens and
     * returns to static positioning at the `md` breakpoint. Use on pages where the heading (title,
     * alert) must remain visible while the content below scrolls on mobile.
     */
    stickyHeading?: boolean;
  };

/**
 * Horizontal gutter every chat-aligned scroll region shares — the page content container,
 * the sticky heading, embedded ChatFrame regions, and the room-builder preview must agree
 * on this recipe or their edges and scrollbar gutters misalign. Change it here only.
 */
export const chatGutterClassNames = 'ps-3 pe-3 md:ps-8 md:pe-5';

const pageContainerContentVariants = cva('w-full', {
  variants: {
    /** The max-width of the main content container. Defaults to 'lg'. */
    size: {
      xl: 'max-w-full',
      lg: 'max-w-[96rem]',
      md: 'max-w-7xl',
      sm: 'max-w-5xl',
      xs: 'max-w-3xl',
    },
    layout: {
      standard: 'mx-auto flex flex-col gap-6 items-start justify-start',
      'fixed-height': 'mx-auto flex flex-col gap-6 items-start justify-start flex-1 min-h-0',
      // Scroll container for chat — full-width, size constraints apply to the inner centering wrapper.
      chat: cn('flex-1 min-h-0 overflow-y-auto scroll-smooth md:[scrollbar-gutter:stable]', chatGutterClassNames, 'pb-8'),
    },
  },
  // Chat content containers are always full-width regardless of `size` — the inner centering wrapper
  // owns the max-width for chat. fixed-height lets `size` govern so the content aligns with the
  // heading at wide viewports (same max-w-[96rem] at size="lg"). Compound variants are required
  // because cva doesn't dedupe conflicting Tailwind classes.
  compoundVariants: [{ layout: 'chat', size: ['lg', 'md', 'sm', 'xs'], className: 'max-w-full' }],
  defaultVariants: {
    size: 'lg',
    layout: 'standard',
  },
});

const pageContainerContentInnerVariants = cva('', {
  variants: {
    layout: {
      standard: 'contents',
      'fixed-height': 'contents',
      chat: 'h-full mx-auto',
    },
  },
  defaultVariants: { layout: 'standard' },
});

// hidden + has-[>*:not(:empty)]:flex, not empty:hidden: a self-gating Alert still mounts an
// empty wrapper div, so this element always has a child and plain `:empty` never matches. Without
// true display:none here, ancestors that promote this via `contents` still gap around it as a
// flex item even at zero height.
const pageContainerHeadingVariants = cva('hidden has-[>*:not(:empty)]:flex w-full mx-auto flex-col gap-6 items-start justify-start', {
  variants: {
    /** The max-width of the main content heading container. Defaults to 'lg'. */
    size: {
      xl: 'max-w-full',
      lg: '',
      md: '',
      sm: '',
      xs: '',
    },
    hasSlots: {
      true: '',
      false: '',
    },
    layout: {
      standard: 'pt-1.5',
      'fixed-height': 'pt-3',
      chat: cn('pt-3 overflow-y-auto md:[scrollbar-gutter:stable]', chatGutterClassNames),
    },
  },
  compoundVariants: [
    { hasSlots: true, size: ['lg', 'md', 'sm', 'xs'], className: 'max-w-[96rem]' },
    { hasSlots: false, size: 'lg', className: 'max-w-[96rem]' },
    { hasSlots: false, size: 'md', className: 'max-w-7xl' },
    { hasSlots: false, size: 'sm', className: 'max-w-5xl' },
    { hasSlots: false, size: 'xs', className: 'max-w-3xl' },
  ],
  defaultVariants: {
    size: 'lg',
    hasSlots: false,
    layout: 'standard',
  },
});

const pageContainerHeaderVariants = cva(
  [
    'transition-[background-color,box-shadow]',
    'shadow-[inset_0_-1px_0_transparent]',
    'flex',
    'gap-3',
    'flex-shrink-0',
    'w-full',
    'ps-3',
    'pe-3',
    'md:ps-8',
    'md:pe-5',
    'relative',
    'z-20',
    // Tighter below `sm`, matching TeacherTopBar's AppBar treatment.
    'min-h-10',
    'py-1',
    'sm:min-h-14',
    'sm:py-2.5',
    'overflow-y-auto',
    'md:[scrollbar-gutter:stable]',
    'empty:hidden',
  ],
  {
    variants: {
      scrolled: {
        true: 'md:bg-surface-background-0 md:shadow-[inset_0_-1px_0_var(--color-border-default)]',
        false: 'bg-transparent',
      },
    },
    defaultVariants: {
      scrolled: false,
    },
  },
);

// Size variants are intentional placeholders — they mirror pageContainerContentVariants so the
// header inner wrapper can be given max-width constraints in a future pass without a breaking API change.
const pageContainerHeaderInnerVariants = cva('flex items-center justify-end gap-2 w-full mx-auto empty:hidden', {
  variants: {
    size: {
      xl: 'max-w-full',
      lg: 'max-w-[96rem]',
      md: 'max-w-[96rem]',
      sm: 'max-w-[96rem]',
      xs: 'max-w-[96rem]',
    },
  },
  defaultVariants: {
    size: 'lg',
  },
});

/** Main column that holds the header bar, scroll area, and bottom bar. Sits beside the Panel slot
 *  and yields width to it when the panel is open. */
const pageContainerMainVariants = cva(
  '@container/main relative flex flex-col flex-1 min-h-0 origin-left overflow-y-auto md:overflow-hidden',
  {
    variants: {
      panelOpen: {
        true: 'w-24 flex-1 md:w-auto',
        false: 'flex-1 w-full',
      },
      layout: {
        standard: '',
        'fixed-height': '',
        chat: 'overflow-hidden',
      },
    },
    defaultVariants: { panelOpen: false, layout: 'standard' },
  },
);

const pageContainerBottomBarVariants = cva([
  'flex',
  'gap-3',
  'flex-shrink-0',
  'w-full',
  'overflow-hidden',
  'ps-3',
  'pe-3',
  'md:ps-8',
  'md:pe-5',
  'md:overflow-y-auto',
  'md:[scrollbar-gutter:stable]',
  'relative',
  'z-10',
  'min-h-0',
  'h-fit',
]);

const pageContainerBodyVariants = cva('flex-1 w-full min-h-0 relative z-10 outline-none', {
  variants: {
    layout: {
      standard: 'md:overflow-y-auto md:[scrollbar-gutter:stable]',
      'fixed-height': 'md:overflow-hidden flex flex-col gap-6 ps-3 pe-3 md:ps-8 md:pe-8',
      chat: 'overflow-hidden flex flex-col gap-6',
    },
  },
  defaultVariants: {
    layout: 'standard',
  },
});

const pageContainerContentAreaVariants = cva('', {
  variants: {
    hasSlots: {
      true: '',
      false: '',
    },
    layout: {
      standard: 'gap-6 ps-3 pe-3 md:ps-8 md:pe-5 pb-8 flex-1 flex flex-col',
      'fixed-height': 'contents',
      chat: 'contents',
    },
  },
  compoundVariants: [
    { hasSlots: true, layout: 'standard', className: 'pt-1.5' },
    { hasSlots: false, layout: 'standard', className: 'pt-8' },
  ],
  defaultVariants: {
    hasSlots: false,
    layout: 'standard',
  },
});

function PageContainerBase({
  className,
  contentClassName,
  breadcrumbs,
  size = 'lg',
  text: textProp = {},
  panelOpen,
  onPanelOpenChange,
  layout = 'standard',
  mainId = 'main-content',
  stickyHeading = false,
  children,
  ...props
}: PageContainerProps) {
  const panelId = useId();
  const mainRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const { scrolled, ref: scrollTrackRef } = useScroll();
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const scrollRef = useCallback(
    (node: HTMLElement | null) => {
      setScrollElement(node);
      scrollTrackRef(node);
    },
    [scrollTrackRef],
  );
  const [mainWidth, setMainWidth] = useState(0);

  useEffect(() => {
    const element = mainRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setMainWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  // Tracks open state for uncontrolled panels (no isOpen prop on Panel slot, no root panelOpen).
  // State lives here so PanelOpen (in Actions) and PanelClose (inside the panel) share it.
  const [uncontrolledPanelOpen, setUncontrolledPanelOpen] = useState(true);

  const { slots } = useSlots(children, {
    actions: { Component: PageContainerActionsSlot },
    alert: { Component: PageContainerAlertSlot },
    breadcrumb: {
      Component: PageContainerBreadcrumbSlot,
      props: { breadcrumbs, 'aria-label': textProp.breadcrumbNavLabel ?? 'Breadcrumb' },
    },
    background: { Component: PageContainerBackgroundSlot },
    content: { Component: PageContainerContentSlot },
    bottom: { Component: PageContainerBottomSlot },
    description: { Component: PageContainerDescriptionSlot },
    titleBadge: { Component: PageContainerTitleBadgeSlot },
    title: { Component: PageContainerTitleSlot },
    panel: {
      Component: PageContainerPanelSlot,
      props: panelOpen !== undefined ? { isOpen: panelOpen, onOpenChange: onPanelOpenChange } : undefined,
    },
    topNav: { Component: PageContainerTopNavSlot },
    topActions: { Component: PageContainerTopActionsSlot },
  });

  const breadcrumbPageLabel = breadcrumbs?.find((b) => b.type === 'page')?.label;
  const hasTitle = Boolean(slots.title);
  const hasTitleSlots = Boolean(hasTitle || slots.titleBadge || slots.description || slots.actions);
  const hasHeaderSlots = Boolean(slots.topActions || breadcrumbs?.length);

  // children is a new reference every render, so this useMemo recalculates on
  // every render. It still avoids re-creating the context object when none of
  // its deps change between renders, which is the primary win here.
  // text is computed inside the memo (not outside) so we depend on textProp
  // rather than a new object reference created each render.
  const contextValue = useMemo(() => {
    // Panel state priority: root props → slot props → uncontrolled (managed here).
    // Uncontrolled means <PageContainer.Panel> with no isOpen prop and no root panelOpen.
    // State lives in PageContainerBase so PanelOpen (in Actions) and PanelClose (inside panel) share it.
    // Collect all panel children to support multiple PageContainer.Panel siblings.
    const panelElements = Children.toArray(children).filter(
      (child): child is ReactElement<PageContainerPanelState> => isValidElement(child) && child.type === PageContainerPanelSlot,
    );
    const firstPanelElement = panelElements[0];

    // Panel context drives PanelClose/PanelToggle for the single uncontrolled panel case.
    // Controlled panels (isOpen prop passed) manage their own state and don't need context.
    let panel: PageContainerPanelState | null;
    if (panelOpen !== undefined) {
      panel = { isOpen: panelOpen, onOpenChange: onPanelOpenChange };
    } else if (!firstPanelElement) {
      panel = null;
    } else if (firstPanelElement.props?.isOpen !== undefined) {
      panel = { isOpen: firstPanelElement.props.isOpen, onOpenChange: firstPanelElement.props.onOpenChange };
    } else {
      panel = { isOpen: uncontrolledPanelOpen, onOpenChange: setUncontrolledPanelOpen };
    }

    const {
      panelLabel = 'Side panel',
      resizePanelLabel = 'Resize panel',
      closePanelLabel = 'Close panel',
      togglePanelLabel = 'Toggle panel',
      breadcrumbNavLabel = 'Breadcrumb',
      headingLabel,
    } = textProp;
    const text = { panelLabel, resizePanelLabel, closePanelLabel, togglePanelLabel, breadcrumbNavLabel, headingLabel };

    // A Panel slot without its own isOpen is driven by the root panelOpen when that is
    // defined (the injected prop wins at render time), so it must not fall through to
    // uncontrolledPanelOpen — a root-controlled CLOSED panel would otherwise read the
    // useState(true) default, flip isOverlayPanel, and inert the page body on narrow
    // containers with no visible panel.
    const anyPanelOpen =
      panelOpen === true ||
      panelElements.some((el) =>
        el.props?.isOpen !== undefined ? el.props.isOpen : panelOpen !== undefined ? false : uncontrolledPanelOpen,
      );
    const isOverlayPanel = Boolean(anyPanelOpen && mainWidth > 0 && mainWidth < SIDE_BY_SIDE_MIN_WIDTH);

    return { panel, panelId, scrolled, text, isOverlayPanel, layout, scrollRef, scrollElement };
  }, [
    children,
    panelId,
    scrolled,
    textProp,
    panelOpen,
    onPanelOpenChange,
    uncontrolledPanelOpen,
    mainWidth,
    layout,
    scrollRef,
    scrollElement,
  ]);

  return (
    <PageContainerProvider value={contextValue}>
      {/* @todo convert to <main> once all routes have a surrounding <main className="contents" id="main-content"> */}
      <div
        ref={mainRef}
        tabIndex={-1}
        // @todo wire id={mainId} back up once we commit to the <main> element
        data-spellbook-name="page-container"
        className={cn('relative flex flex-1 min-h-0 w-full overflow-hidden isolate @container/page outline-none', className)}
        {...props}
      >
        <section
          ref={sectionRef}
          aria-label={textProp.contentLabel}
          className={pageContainerMainVariants({ panelOpen: contextValue.panel?.isOpen ?? false, layout })}
        >
          {!hasTitle && breadcrumbPageLabel && <h1 className="sr-only">{breadcrumbPageLabel}</h1>}
          {slots.background}
          {hasHeaderSlots && (
            <div className={pageContainerHeaderVariants({ scrolled })}>
              <div className={pageContainerHeaderInnerVariants({ size })}>
                {slots.breadcrumb}
                {slots.topActions}
              </div>
            </div>
          )}
          <div
            ref={layout === 'standard' ? scrollRef : undefined}
            data-spellbook-name="page-container-body"
            inert={contextValue.isOverlayPanel || undefined}
            className={pageContainerBodyVariants({ layout })}
          >
            <div className={cn(pageContainerContentAreaVariants({ hasSlots: hasHeaderSlots, layout }), contentClassName)}>
              {slots.topNav && (
                <div data-spellbook-name="page-top-nav" className={pageContainerHeadingVariants({ hasSlots: !!slots.topNav, size })}>
                  {slots.topNav}
                </div>
              )}
              <div
                data-spellbook-name="page-heading"
                className={cn(
                  pageContainerHeadingVariants({ hasSlots: hasHeaderSlots, size, layout }),
                  stickyHeading && 'sticky top-0 z-10 bg-surface-background-default pb-3 md:static md:bg-transparent md:pb-0',
                )}
              >
                {hasTitleSlots && (
                  <header data-spellbook-name="page-container-header" className="flex w-full" aria-label={textProp.headingLabel}>
                    <div className="flex flex-col @sm/page:flex-row @sm/page:flex-wrap w-full items-start @sm/page:justify-end gap-2">
                      <div className="flex flex-1 flex-col gap-1">
                        <div className="flex items-center gap-2">
                          {slots.title}
                          {slots.titleBadge}
                        </div>
                        {slots.description}
                      </div>
                      <div className="w-fit self-start">{slots.actions}</div>
                    </div>
                  </header>
                )}
                {slots.alert}
              </div>
              <div
                ref={layout === 'chat' ? scrollRef : undefined}
                data-spellbook-name="page-content-container"
                className={pageContainerContentVariants({ size, layout })}
              >
                <div className={pageContainerContentInnerVariants({ layout })}>{slots.content}</div>
              </div>
            </div>
          </div>

          {slots.bottom && (
            <div className={pageContainerBottomBarVariants()}>
              <div className={cn(pageContainerHeaderInnerVariants({ size }), 'mt-auto')}>{slots.bottom}</div>
            </div>
          )}
        </section>
        {slots.panel}
      </div>
    </PageContainerProvider>
  );
}

/**
 * Full-height page layout wrapper that provides a consistent structure for page titles, breadcrumbs, header actions, scrollable content, and an optional resizable side panel.
 *
 * ### When to use
 * - Any full-page view that needs a title, breadcrumbs, or header actions
 * - Pages with a side panel alongside main content (chat interfaces, document editors, split-screen tools)
 * - Layouts with a fixed bottom action bar that stays anchored as content scrolls
 *
 * ### When NOT to use
 * - Content sections within a page: use `Card` instead
 * - Modal or dialog layouts: use `Modal` or `Drawer` instead
 * - Nested inside another `PageContainer`: top-level page wrapper only, nesting is not supported
 *
 * ### API
 * - `breadcrumbs`: auto-renders the Breadcrumb slot. Last entry should have `type: 'page'` — also used as a visually-hidden `h1` fallback when no `<PageContainer.Title>` is present.
 * - `size`: max-width of the content column. `'xl'` full-width, `'lg'` (default, 96rem) standard wide, `'md'` (80rem) medium, `'sm'` (60rem) narrower, `'xs'` (48rem) focused reading.
 * - `layout`: `'standard'` (default) scrolls the content area. `'fixed-height'` fills remaining flex space — use for chat or editors where an inner component owns scrolling.
 * - `className`, `id`, `style`, `data-*`, `aria-*`: standard `<div>` props spread onto the outermost element. Use `className` to add height constraints when the surrounding layout does not provide them — e.g. `className="h-dvh"` for standalone public pages.
 * - `mainId`: currently a no-op — reserved for when `PageContainer` takes over the `<main>` landmark role. The surrounding layout shell is responsible for the skip-to-content target in the interim.
 * - `contentClassName`: classes for the scrollable content area wrapper.
 * - `text`: override user-facing strings. All keys optional, fall back to English defaults. Keys: `breadcrumbNavLabel`, `panelLabel`, `resizePanelLabel`, `closePanelLabel`, `togglePanelLabel`, `headingLabel`, `contentLabel` (names the content section as a `region` landmark; no default).
 * - `panelOpen` / `onPanelOpenChange`: root-level panel control. Use when panel state must be read or driven from outside.
 * - `usePageContainerScrollRef()`: on `layout="fixed-height"` pages, call this hook inside the component that owns its own scroll container and attach the ref so the header shadow tracks that element instead of the non-scrolling PageContainer body.
 *
 * ### Composition (slots)
 * - `<PageContainer.Background>`: decorative aurora gradient. `aria-hidden`. Accepts `variant="aurora-top"` (default) or `variant="aurora-bottom"`. Not rendered by default.
 * - `<PageContainer.Breadcrumb>`: auto-rendered when `breadcrumbs` is provided. Renders at all screen sizes. Compose manually for custom implementations.
 * - `<PageContainer.Title>`: page heading as `h1`. Include on every page with a visible title.
 * - `<PageContainer.TitleBadge>`: badge inline with the title. Not rendered by default.
 * - `<PageContainer.Description>`: descriptive text below the title, rendered as `body-2`. Not rendered by default.
 * - `<PageContainer.Actions>`: heading-aligned action buttons. Not rendered by default.
 * - `<PageContainer.TopActions>`: header bar action buttons, right-aligned. Not rendered by default.
 * - `<PageContainer.TopNav>`: navigation area above the sticky header and heading. Not rendered by default.
 * - `<PageContainer.Alert>`: notification area above main content. The slot carries no live-region role — the child (e.g. `<Alert>`) is responsible for its own `role`. Not rendered by default.
 * - `<PageContainer.Content>`: main scrollable content area. Always include in JSX.
 * - `<PageContainer.Bottom>`: bottom-anchored area for fixed footers or save/cancel bars. Not rendered by default.
 * - `<PageContainer.Panel>`: right-side resizable panel. Three usage patterns: (1) **Uncontrolled** (no `isOpen`): state managed internally, `PanelToggle`/`PanelClose` work automatically. (2) **Slot-controlled** (`isOpen` + `onOpenChange` on the slot). (3) **Root-controlled** (`panelOpen`/`onPanelOpenChange` on `<PageContainer>`). To cap max-width use `@2xl/page:max-w-[Xpx]` or `!max-w-[Xpx]`; plain `max-w-*` is overridden by internal container query styles. Keyboard: `Escape` closes. Not rendered by default.
 * - `<PageContainer.PanelClose>`: closes the panel. Renders a ghost X `IconButton` by default. Use `asChild` for a custom trigger.
 * - `<PageContainer.PanelToggle>`: toggles the panel. Renders a ghost `IconButton` by default. Use `asChild` for a custom trigger. Exposes `aria-expanded` and `aria-controls`.
 *
 * ### Layout behavior
 * - **`layout="standard"`** (default): the Content slot scrolls vertically inside `PageContainer`.
 * - **`layout="fixed-height"`**: the Content slot fills all remaining vertical space. The inner component manages its own scroll — use `usePageContainerScrollRef()` inside that child to track scroll position for the header shadow.
 * - The header bar is not rendered when neither `TopActions` nor `breadcrumbs` are present; the Content area adds top padding to compensate.
 * - Mobile left padding reserves space for the sidebar navigation button.
 * - Scrollbar gutter is stable to prevent layout shift when content overflows.
 * - PageContainer relies on a height-constrained ancestor or `className="h-dvh"` on itself. Without a height constraint the flex-fill chain collapses.
 *
 * ### Accessibility
 * - PageContainer currently renders as `<div>` — the `<main>` landmark and skip-to-content target are supplied by the surrounding layout shell (`AppLayout`, `EmptyLayout`) via `<main className="contents" id="main-content">`. This is temporary until the chat layout refactor is complete, after which PageContainer will take over as `<main>` directly. The page-heading area renders as `<header>` inside a `<section>`.
 * - Every page is guaranteed an `h1`: `<PageContainer.Title>` renders as `h1`; if absent but `breadcrumbs` includes a `type="page"` entry a visually-hidden `h1` is injected automatically; if neither is present no fallback `h1` is injected — include a `<PageContainer.Title>` or a `type='page'` breadcrumb entry on every page.
 * - `Background` is `aria-hidden` — decorative gradients are invisible to assistive technology.
 * - `Alert` carries no live-region role itself — the child component owns its `role`.
 * - `Panel` carries `role="complementary"` (from `<aside>`) and an `aria-label` (default `"Side panel"`). Focus moves into the panel on open and returns on close.
 * - In overlay mode (narrow screens where the panel covers full width) the panel gains `aria-modal="true"` and main content becomes `inert`.
 * - The resize handle is keyboard-accessible: `ArrowLeft` / `ArrowRight` adjusts width. Only visible where side-by-side layout is possible.
 */
export const PageContainer = Object.assign(PageContainerBase, {
  Actions: PageContainerActionsSlot,
  Alert: PageContainerAlertSlot,
  Background: PageContainerBackgroundSlot,
  Bottom: PageContainerBottomSlot,
  Breadcrumb: PageContainerBreadcrumbSlot,
  Content: PageContainerContentSlot,
  Description: PageContainerDescriptionSlot,
  TitleBadge: PageContainerTitleBadgeSlot,
  Title: PageContainerTitleSlot,
  Panel: PageContainerPanelSlot,
  PanelClose: PageContainerPanelCloseSlot,
  PanelToggle: PageContainerPanelToggleSlot,
  TopNav: PageContainerTopNavSlot,
  TopActions: PageContainerTopActionsSlot,
});
